import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import service from '../../services/systemAnnouncementService';
import { useSystemAnnouncements } from '../../contexts/SystemAnnouncementsContext';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import { announcementButton, announcementPanel } from './AnnouncementInbox';

const blank = () => ({ title: '', body: '', severity: 'info', expires_at: null });
const inputClass = 'mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';
const draftOf = row => ({ title: row.title, body: row.body, severity: row.severity, expires_at: row.expires_at });

export default function SystemAnnouncementManager({ entity = false }) {
  const value = useSystemAnnouncements();
  const entityId = entity ? value.scope?.entityId : null;
  const scope = useMemo(() => Object.freeze({ type: entity ? 'entity' : 'global', entityId }), [entity, entityId]);
  const owner = `${value.owner}:${scope.type}:${entityId}:${entity ? value.projectionVersion : ''}`;
  const ownerRef = useRef(owner);
  // This ref guards async publications, never rendered content. Updating it in
  // render closes the old audience before layout/passive cleanup can run.
  // eslint-disable-next-line react-hooks/refs
  ownerRef.current = owner;
  const isOwnerCurrent = useCallback(() => ownerRef.current === owner, [owner]);
  // Remount all editor/confirmation/uncertain-create state at the audience
  // boundary. The render-time fence retires old callbacks before cleanup.
  if (entity && !entityId) return null;
  return <AnnouncementManager key={owner} scope={scope} audienceName={value.entityName}
    isOwnerCurrent={isOwnerCurrent} />;
}

function AnnouncementManager({ scope, audienceName, isOwnerCurrent }) {
  const { t } = useTranslation();
  const { store } = useSystemAnnouncements();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(blank);
  const [expiryInput, setExpiryInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [uncertainCreate, setUncertainCreate] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [entityEnabled, setEntityEnabled] = useState(scope.type === 'global' ? true : null);
  const generation = useRef(0);
  const controllers = useRef(new Set());
  const mounted = useRef(true);
  const isCurrent = useCallback(value => mounted.current && isOwnerCurrent() && store.live() && value === generation.current, [store, isOwnerCurrent]);
  const load = useCallback(async (cursor = null) => {
    const current = ++generation.current;
    controllers.current.forEach(controller => controller.abort()); controllers.current.clear();
    const controller = new AbortController(); controllers.current.add(controller);
    setBusy(true); setError(null);
    try {
      const data = await service.manage(scope, cursor, controller.signal);
      if (!isCurrent(current)) return;
      setItems(previous => cursor ? [...previous, ...data.items] : data.items);
      setNextCursor(data.nextCursor); setUncertainCreate(false);
      setEntityEnabled(scope.type === 'global' || data.entityAnnouncementsEnabled === true);
    } catch {
      if (isCurrent(current) && !controller.signal.aborted) setError('announcements.loadError');
    } finally {
      controllers.current.delete(controller);
      if (isCurrent(current)) setBusy(false);
    }
  }, [isCurrent, scope]);
  useEffect(() => {
    mounted.current = true; void load();
    const activeControllers = controllers.current;
    return () => { mounted.current = false; generation.current += 1; activeControllers.forEach(controller => controller.abort()); };
  }, [load]);
  const select = row => {
    setSelected(row); setDraft(row ? draftOf(row) : blank());
    setExpiryInput(row?.expires_at?.replace(/Z$/, '') || ''); setError(null);
  };
  const expiryInstant = expiryInput ? Date.parse(`${expiryInput}Z`) : NaN;
  const normalizedExpiry = expiryInput ? Number.isFinite(expiryInstant) ? new Date(expiryInstant).toISOString() : `${expiryInput}Z` : null;
  const submitted = { ...draft, expires_at: normalizedExpiry };
  const dirty = selected && JSON.stringify(submitted) !== JSON.stringify(draftOf(selected));
  const writable = entityEnabled && (!selected || selected.status === 'draft');
  const audience = scope.type === 'entity' ? t('announcements.entityAudience', { entity: audienceName }) : t('announcements.allUsers');

  const perform = async action => {
    if (busy || !isOwnerCurrent() || !store.live() || (!entityEnabled && action !== 'withdraw')) return;
    const current = ++generation.current;
    const controller = new AbortController(); controllers.current.add(controller);
    setBusy(true); setError(null);
    const creating = action === 'save' && !selected;
    try {
      let row;
      if (action === 'save') row = selected
        ? await service.edit(scope, selected.id, { ...submitted, revision: selected.revision }, controller.signal)
        : await service.create(scope, submitted, controller.signal);
      else row = await service.transition(scope, selected.id, action, selected.revision, controller.signal);
      if (!isCurrent(current)) return;
      select(row); setConfirmation(null);
      setItems(previous => [row, ...previous.filter(item => item.id !== row.id)]);
      // Publication is durable before either projection is refreshed. An error
      // here must never replay the mutation, especially an uncertain create.
      if (action !== 'save') { store.invalidate(); await store.refresh({ force: true }); }
      if (isCurrent(current)) await load();
    } catch (failure) {
      if (!isCurrent(current) || controller.signal.aborted) return;
      const status = failure.response?.status;
      if (creating && (!status || status >= 500)) {
        setUncertainCreate(true); setError('announcements.createUncertain');
      } else if (failure.response?.data?.error?.code === 'ENTITY_ANNOUNCEMENTS_DISABLED') {
        setEntityEnabled(false); setError('announcements.entityDisabled');
      } else setError(status === 409 ? 'announcements.conflict' : status === 400 ? 'announcements.invalidDraft' : 'announcements.actionError');
      setConfirmation(null);
    } finally {
      controllers.current.delete(controller);
      if (isCurrent(current)) setBusy(false);
    }
  };
  // Management labels use the server's sampled expiry decision. An incorrect
  // workstation clock must not relabel a currently published announcement.
  const labelFor = row => row.status === 'published' && row.expired ? 'expired' : row.status;
  return (
    <main className="mx-auto w-full max-w-5xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t(scope.type === 'entity' ? 'announcements.manageEntity' : 'announcements.manageGlobal')}</h1>
        <Link to="/announcements" className="text-blue-600 underline dark:text-blue-300">{t('announcements.back')}</Link>
      </div>
      <p className="break-words text-sm text-gray-600 dark:text-gray-300">{t('announcements.audienceLabel')}: {audience}</p>
      {scope.type === 'entity' && entityEnabled === false && <div className={announcementPanel}>
        <p>{t('announcements.entityDisabled')}</p>
        <Link className="text-blue-600 underline dark:text-blue-300" to="/settings/organization">{t('announcements.organizationSettings')}</Link>
      </div>}
      {error && <p role="alert" className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-950">{t(error)}</p>}
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <section className={`${announcementPanel} min-w-0 space-y-3`} aria-label={t('announcements.records')}>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={announcementButton} disabled={busy || uncertainCreate || !entityEnabled} onClick={() => select(null)}>{t('announcements.new')}</button>
            <button type="button" className={announcementButton} disabled={busy} onClick={() => { setSelected(null); setDraft(blank()); setExpiryInput(''); void load(); }}>{t('announcements.refresh')}</button>
          </div>
          {busy && <p role="status">{t('announcements.loading')}</p>}
          <ul className="space-y-2">{items.map(row => <li key={row.id}>
            <button type="button" disabled={busy} onClick={() => select(row)} className="w-full rounded border border-gray-200 p-3 text-left hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700">
              <span className="block break-words font-semibold">{row.title}</span>
              <span className="text-xs">{t(`announcements.status.${labelFor(row)}`)}</span>
            </button>
          </li>)}</ul>
          {nextCursor && <button type="button" disabled={busy} className={announcementButton} onClick={() => load(nextCursor)}>{t('announcements.loadMore')}</button>}
        </section>
        <form className={`${announcementPanel} min-w-0 space-y-4`} onSubmit={event => { event.preventDefault(); void perform('save'); }}>
          <p className="break-words text-sm">{t('announcements.audienceLabel')}: {audience}</p>
          <label className="block">{t('announcements.subject')}<input className={inputClass} required maxLength={200} value={draft.title} disabled={busy || !writable} onChange={event => setDraft({ ...draft, title: event.target.value })} /></label>
          <label className="block">{t('announcements.message')}<textarea className={inputClass} required maxLength={6000} rows={8} value={draft.body} disabled={busy || !writable} onChange={event => setDraft({ ...draft, body: event.target.value })} /></label>
          <p className="text-sm text-gray-600 dark:text-gray-300">{t('announcements.authoringGuidance')}</p>
          <label className="block">{t('announcements.severityLabel')}<select className={inputClass} value={draft.severity} disabled={busy || !writable} onChange={event => setDraft({ ...draft, severity: event.target.value })}>
            {['info', 'warning', 'critical'].map(value => <option key={value} value={value}>{t(`announcements.severity.${value}`)}</option>)}
          </select></label>
          <label className="block">{t('announcements.expiresAt')}<input className={inputClass} type="datetime-local" step="0.001" value={expiryInput} disabled={busy || !writable} onChange={event => setExpiryInput(event.target.value)} /></label>
          <button type="button" className={announcementButton} disabled={busy || !writable} onClick={() => setExpiryInput('')}>{t('announcements.noExpiry')}</button>
          <p className="text-sm text-gray-600 dark:text-gray-300">{t('announcements.expiryHelp')}</p>
          <div className="flex flex-wrap gap-2">
            {writable && <button className={`${announcementButton} bg-blue-600 text-white hover:bg-blue-700`} type="submit" disabled={busy || uncertainCreate}>{t('announcements.save')}</button>}
            {selected?.status === 'draft' && <button className={announcementButton} type="button" disabled={busy || dirty || !entityEnabled} onClick={() => setConfirmation('publish')}>{t('announcements.publish')}</button>}
            {selected?.status === 'published' && <button className={announcementButton} type="button" disabled={busy} onClick={() => setConfirmation('withdraw')}>{t('announcements.withdraw')}</button>}
          </div>
          {dirty && <p className="text-sm">{t('announcements.saveBeforePublish')}</p>}
          {selected && selected.status !== 'draft' && <p className="text-sm">{t('announcements.immutable')}</p>}
        </form>
      </div>
      <ConfirmDialogShell isOpen={Boolean(confirmation)} onClose={() => setConfirmation(null)}
        onConfirm={() => perform(confirmation)} isLoading={busy} confirmVariant={confirmation === 'withdraw' ? 'danger' : 'primary'}
        title={t(confirmation === 'withdraw' ? 'announcements.confirmWithdraw' : 'announcements.confirmPublish')}>
        <p className="break-words">{t('announcements.audienceLabel')}: {audience}</p>
        <p>{t(`announcements.severity.${selected?.severity || 'info'}`)}</p>
        <p className="break-words font-semibold">{selected?.title}</p>
        <p className="max-h-64 overflow-auto whitespace-pre-wrap break-words">{selected?.body}</p>
        <p>{selected?.expires_at ? `${t('announcements.expiresAt')}: ${selected.expires_at.replace('T', ' ').replace('Z', ' UTC')}` : t('announcements.noExpiry')}</p>
      </ConfirmDialogShell>
    </main>
  );
}
